var _jsxFileName = "/media/data/Sandbox/soapbox-defake/app/soapbox/components/status.tsx";
import "core-js/modules/web.dom-collections.iterator.js";
import classNames from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { HotKeys } from 'react-hotkeys';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { NavLink, useHistory } from 'react-router-dom';
import { mentionCompose, replyCompose } from 'soapbox/actions/compose';
import { toggleFavourite, toggleReblog } from 'soapbox/actions/interactions';
import { openModal } from 'soapbox/actions/modals';
import { toggleStatusHidden } from 'soapbox/actions/statuses';
import Icon from 'soapbox/components/icon';
import TranslateButton from 'soapbox/components/translate-button';
import AccountContainer from 'soapbox/containers/account-container';
import QuotedStatus from 'soapbox/features/status/containers/quoted-status-container';
import { useAppDispatch, useSettings } from 'soapbox/hooks';
import { defaultMediaVisibility, textForScreenReader, getActualStatus } from 'soapbox/utils/status';
import StatusActionBar from './status-action-bar';
import StatusContent from './status-content';
import StatusMedia from './status-media';
import StatusReplyMentions from './status-reply-mentions';
import SensitiveContentOverlay from './statuses/sensitive-content-overlay';
import { Card, HStack, Stack, Text } from './ui';
const messages = defineMessages({
  reblogged_by: {
    "id": "status.reblogged_by",
    "defaultMessage": "{name} reposted"
  }
});

const Status = props => {
  const {
    status,
    focusable = true,
    hoverable = true,
    onClick,
    onMoveUp,
    onMoveDown,
    muted,
    hidden,
    featured,
    unread,
    hideActionBar,
    variant = 'rounded',
    withDismiss
  } = props;
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const settings = useSettings();
  const displayMedia = settings.get('displayMedia');
  const didShowCard = useRef(false);
  const node = useRef(null);
  const overlay = useRef(null);
  const [showMedia, setShowMedia] = useState(defaultMediaVisibility(status, displayMedia));
  const [minHeight, setMinHeight] = useState(208);
  const actualStatus = getActualStatus(status);
  const statusUrl = "/@".concat(actualStatus.getIn(['account', 'acct']), "/posts/").concat(actualStatus.id); // Track height changes we know about to compensate scrolling.

  useEffect(() => {
    didShowCard.current = Boolean(!muted && !hidden && (status === null || status === void 0 ? void 0 : status.card));
  }, []);
  useEffect(() => {
    setShowMedia(defaultMediaVisibility(status, displayMedia));
  }, [status.id]);
  useEffect(() => {
    if (overlay.current) {
      setMinHeight(overlay.current.getBoundingClientRect().height);
    }
  }, [overlay.current]);

  const handleToggleMediaVisibility = () => {
    setShowMedia(!showMedia);
  };

  const handleClick = e => {
    var _getSelection;

    e === null || e === void 0 ? void 0 : e.stopPropagation(); // If the user is selecting text, don't focus the status.

    if ((_getSelection = getSelection()) !== null && _getSelection !== void 0 && _getSelection.toString().length) {
      return;
    }

    if (!e || !(e.ctrlKey || e.metaKey)) {
      if (onClick) {
        onClick();
      } else {
        history.push(statusUrl);
      }
    } else {
      window.open(statusUrl, '_blank');
    }
  };

  const handleHotkeyOpenMedia = e => {
    const status = actualStatus;
    const firstAttachment = status.media_attachments.first();
    e === null || e === void 0 ? void 0 : e.preventDefault();

    if (firstAttachment) {
      if (firstAttachment.type === 'video') {
        dispatch(openModal('VIDEO', {
          status,
          media: firstAttachment,
          time: 0
        }));
      } else {
        dispatch(openModal('MEDIA', {
          status,
          media: status.media_attachments,
          index: 0
        }));
      }
    }
  };

  const handleHotkeyReply = e => {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    dispatch(replyCompose(actualStatus));
  };

  const handleHotkeyFavourite = () => {
    toggleFavourite(actualStatus);
  };

  const handleHotkeyBoost = e => {
    const modalReblog = () => dispatch(toggleReblog(actualStatus));

    const boostModal = settings.get('boostModal');

    if (e && e.shiftKey || !boostModal) {
      modalReblog();
    } else {
      dispatch(openModal('BOOST', {
        status: actualStatus,
        onReblog: modalReblog
      }));
    }
  };

  const handleHotkeyMention = e => {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    dispatch(mentionCompose(actualStatus.account));
  };

  const handleHotkeyOpen = () => {
    history.push(statusUrl);
  };

  const handleHotkeyOpenProfile = () => {
    history.push("/@".concat(actualStatus.getIn(['account', 'acct'])));
  };

  const handleHotkeyMoveUp = e => {
    if (onMoveUp) {
      onMoveUp(status.id, featured);
    }
  };

  const handleHotkeyMoveDown = e => {
    if (onMoveDown) {
      onMoveDown(status.id, featured);
    }
  };

  const handleHotkeyToggleHidden = () => {
    dispatch(toggleStatusHidden(actualStatus));
  };

  const handleHotkeyToggleSensitive = () => {
    handleToggleMediaVisibility();
  };

  const handleHotkeyReact = () => {
    _expandEmojiSelector();
  };

  const _expandEmojiSelector = () => {
    var _node$current;

    const firstEmoji = (_node$current = node.current) === null || _node$current === void 0 ? void 0 : _node$current.querySelector('.emoji-react-selector .emoji-react-selector__emoji');
    firstEmoji === null || firstEmoji === void 0 ? void 0 : firstEmoji.focus();
  };

  if (!status) return null;
  let rebloggedByText, reblogElement, reblogElementMobile;

  if (hidden) {
    return /*#__PURE__*/React.createElement("div", {
      ref: node,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 210,
        columnNumber: 7
      }
    }, actualStatus.getIn(['account', 'display_name']) || actualStatus.getIn(['account', 'username']), actualStatus.content);
  }

  if (status.filtered || actualStatus.filtered) {
    const minHandlers = muted ? undefined : {
      moveUp: handleHotkeyMoveUp,
      moveDown: handleHotkeyMoveDown
    };
    return /*#__PURE__*/React.createElement(HotKeys, {
      handlers: minHandlers,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 224,
        columnNumber: 7
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames('status__wrapper', 'status__wrapper--filtered', {
        focusable
      }),
      tabIndex: focusable ? 0 : undefined,
      ref: node,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 225,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "status.filtered",
      defaultMessage: "Filtered",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 226,
        columnNumber: 11
      }
    })));
  }

  if (status.reblog && typeof status.reblog === 'object') {
    const displayNameHtml = {
      __html: String(status.getIn(['account', 'display_name_html']))
    };
    reblogElement = /*#__PURE__*/React.createElement(NavLink, {
      to: "/@".concat(status.getIn(['account', 'acct'])),
      onClick: event => event.stopPropagation(),
      className: "hidden sm:flex items-center text-gray-700 dark:text-gray-600 text-xs font-medium space-x-1 hover:underline",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 236,
        columnNumber: 7
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: require('@tabler/icons/repeat.svg'),
      className: "text-green-600",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 241,
        columnNumber: 9
      }
    }), /*#__PURE__*/React.createElement(HStack, {
      alignItems: "center",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 243,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "status.reblogged_by",
      defaultMessage: "{name} reposted",
      values: {
        name: /*#__PURE__*/React.createElement("bdi", {
          className: "max-w-[100px] truncate pr-1",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 248,
            columnNumber: 21
          }
        }, /*#__PURE__*/React.createElement("strong", {
          className: "text-gray-800 dark:text-gray-200",
          dangerouslySetInnerHTML: displayNameHtml,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 249,
            columnNumber: 17
          }
        }))
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 244,
        columnNumber: 11
      }
    })));
    reblogElementMobile = /*#__PURE__*/React.createElement("div", {
      className: "pb-5 -mt-2 sm:hidden truncate",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 258,
        columnNumber: 7
      }
    }, /*#__PURE__*/React.createElement(NavLink, {
      to: "/@".concat(status.getIn(['account', 'acct'])),
      onClick: event => event.stopPropagation(),
      className: "flex items-center text-gray-700 dark:text-gray-600 text-xs font-medium space-x-1 hover:underline",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 259,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: require('@tabler/icons/repeat.svg'),
      className: "text-green-600",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 264,
        columnNumber: 11
      }
    }), /*#__PURE__*/React.createElement("span", {
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 266,
        columnNumber: 11
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "status.reblogged_by",
      defaultMessage: "{name} reposted",
      values: {
        name: /*#__PURE__*/React.createElement("bdi", {
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 271,
            columnNumber: 23
          }
        }, /*#__PURE__*/React.createElement("strong", {
          className: "text-gray-800 dark:text-gray-200",
          dangerouslySetInnerHTML: displayNameHtml,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 272,
            columnNumber: 19
          }
        }))
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 267,
        columnNumber: 13
      }
    }))));
    rebloggedByText = intl.formatMessage(messages.reblogged_by, {
      name: String(status.getIn(['account', 'acct']))
    });
  }

  let quote;

  if (actualStatus.quote) {
    if (actualStatus.pleroma.get('quote_visible', true) === false) {
      quote = /*#__PURE__*/React.createElement("div", {
        className: "quoted-status-tombstone",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 292,
          columnNumber: 9
        }
      }, /*#__PURE__*/React.createElement("p", {
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 293,
          columnNumber: 11
        }
      }, /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "statuses.quote_tombstone",
        defaultMessage: "Post is unavailable.",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 293,
          columnNumber: 14
        }
      })));
    } else {
      quote = /*#__PURE__*/React.createElement(QuotedStatus, {
        statusId: actualStatus.quote,
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 297,
          columnNumber: 15
        }
      });
    }
  }

  const handlers = muted ? undefined : {
    reply: handleHotkeyReply,
    favourite: handleHotkeyFavourite,
    boost: handleHotkeyBoost,
    mention: handleHotkeyMention,
    open: handleHotkeyOpen,
    openProfile: handleHotkeyOpenProfile,
    moveUp: handleHotkeyMoveUp,
    moveDown: handleHotkeyMoveDown,
    toggleHidden: handleHotkeyToggleHidden,
    toggleSensitive: handleHotkeyToggleSensitive,
    openMedia: handleHotkeyOpenMedia,
    react: handleHotkeyReact
  };
  const accountAction = props.accountAction || reblogElement;
  const isUnderReview = actualStatus.visibility === 'self';
  const isSensitive = actualStatus.hidden;
  return /*#__PURE__*/React.createElement(HotKeys, {
    handlers: handlers,
    "data-testid": "status",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 322,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames('status cursor-pointer', {
      focusable
    }),
    tabIndex: focusable && !muted ? 0 : undefined,
    "data-featured": featured ? 'true' : null,
    "aria-label": textForScreenReader(intl, actualStatus, rebloggedByText),
    ref: node,
    onClick: handleClick,
    role: "link",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 323,
      columnNumber: 7
    }
  }, featured && /*#__PURE__*/React.createElement("div", {
    className: "pt-4 px-4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 333,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    space: 1,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 334,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: require('@tabler/icons/pinned.svg'),
    className: "text-gray-600 dark:text-gray-400",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 335,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Text, {
    size: "sm",
    theme: "muted",
    weight: "medium",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 337,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "status.pinned",
    defaultMessage: "Pinned post",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 338,
      columnNumber: 17
    }
  })))), /*#__PURE__*/React.createElement(Card, {
    variant: variant,
    className: classNames('status__wrapper', "status-".concat(actualStatus.visibility), {
      'py-6 sm:p-5': variant === 'rounded',
      'status-reply': !!status.in_reply_to_id,
      muted,
      read: unread === false
    }),
    "data-id": status.id,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 344,
      columnNumber: 9
    }
  }, reblogElementMobile, /*#__PURE__*/React.createElement("div", {
    className: "mb-4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 356,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(AccountContainer, {
    key: String(actualStatus.getIn(['account', 'id'])),
    id: String(actualStatus.getIn(['account', 'id'])),
    timestamp: actualStatus.created_at,
    timestampUrl: statusUrl,
    action: accountAction,
    hideActions: !accountAction,
    showEdit: !!actualStatus.edited_at,
    showProfileHoverCard: hoverable,
    withLinkToProfile: hoverable,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 357,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "status__content-wrapper",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 370,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(StatusReplyMentions, {
    status: actualStatus,
    hoverable: hoverable,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 371,
      columnNumber: 13
    }
  }), /*#__PURE__*/React.createElement(Stack, {
    className: "relative z-0",
    style: {
      minHeight: isUnderReview || isSensitive ? Math.max(minHeight, 208) + 12 : undefined
    },
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 373,
      columnNumber: 13
    }
  }, (isUnderReview || isSensitive) && /*#__PURE__*/React.createElement(SensitiveContentOverlay, {
    status: status,
    visible: showMedia,
    onToggleVisibility: handleToggleMediaVisibility,
    ref: overlay,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 378,
      columnNumber: 17
    }
  }), /*#__PURE__*/React.createElement(Stack, {
    space: 4,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 386,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(StatusContent, {
    status: actualStatus,
    onClick: handleClick,
    collapsable: true,
    translatable: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 387,
      columnNumber: 17
    }
  }), /*#__PURE__*/React.createElement(TranslateButton, {
    status: actualStatus,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 394,
      columnNumber: 17
    }
  }), (quote || actualStatus.card || actualStatus.media_attachments.size > 0) && /*#__PURE__*/React.createElement(Stack, {
    space: 4,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 397,
      columnNumber: 19
    }
  }, /*#__PURE__*/React.createElement(StatusMedia, {
    status: actualStatus,
    muted: muted,
    onClick: handleClick,
    showMedia: showMedia,
    onToggleVisibility: handleToggleMediaVisibility,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 398,
      columnNumber: 21
    }
  }), quote))), !hideActionBar && !isUnderReview && /*#__PURE__*/React.createElement("div", {
    className: "pt-4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 413,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(StatusActionBar, {
    status: actualStatus,
    withDismiss: withDismiss,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 414,
      columnNumber: 17
    }
  }))))));
};

export default Status;